import { OneOnOneNotesIcon } from "components/icons";
import { LoadingWrapper } from "components/common/layouts";
import LoadingIndicator from "components/common/layouts/LoadingIndicator";
import { Button } from "components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "components/ui/sheet";
import { cn } from "lib/utils";
import { useEffect, useRef, useState } from "react";
import { OneOnOneNotesResponse } from "responses";
import {
  RISK_LEVEL_DISPLAY,
  RiskLevel,
  formatCatchUpDate,
} from "utils/one-on-one-notes.util";
import { useConsolidatedDrawer } from "./ConsolidatedDrawerContext";
import OneOnOneNoteDetail from "./OneOnOneNoteDetail";

type OneOnOneNotesSheetProps = {
  notes: OneOnOneNotesResponse[];
  isLoading?: boolean;
  showViewMore?: boolean;
  isFetchingNextPage?: boolean;
  onViewMore?: () => void;
};

const OneOnOneNotesSheet = ({
  notes,
  isLoading = false,
  showViewMore = false,
  isFetchingNextPage = false,
  onViewMore,
}: OneOnOneNotesSheetProps) => {
  const {
    isOpen: open,
    selectedId: selectedNoteId,
    shouldScrollToList: shouldScrollToAllNotes,
    openDrawer,
    onOpenChange,
  } = useConsolidatedDrawer<string>();
  const detailSectionRef = useRef<HTMLDivElement | null>(null);
  const allNotesSectionRef = useRef<HTMLDivElement | null>(null);
  const [shouldScrollToDetail, setShouldScrollToDetail] = useState(false);
  const selectedNote = selectedNoteId
    ? notes.find((note) => note.id === selectedNoteId)
    : undefined;

  useEffect(() => {
    if (!open) {
      setShouldScrollToDetail(false);
      return;
    }

    if (!shouldScrollToDetail || isLoading || !detailSectionRef.current) {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      detailSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setShouldScrollToDetail(false);
    });

    return () => cancelAnimationFrame(frameId);
  }, [open, shouldScrollToDetail, isLoading]);

  useEffect(() => {
    if (!open || !shouldScrollToAllNotes || !allNotesSectionRef.current) {
      return;
    }

    allNotesSectionRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [open, shouldScrollToAllNotes, notes.length]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" showCloseButton={false}>
        <SheetHeader>
          <div className="flex items-center gap-2">
            <div className="shrink-0 rounded-full bg-turquoise-10 p-1 text-turquoise-100">
              <OneOnOneNotesIcon />
            </div>
            <SheetTitle className="body-20-semibold">1-on-1 Notes</SheetTitle>
          </div>
          <SheetClose />
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          <LoadingWrapper isLoading={isLoading}>
            <div ref={detailSectionRef}>
              {selectedNote && <OneOnOneNoteDetail note={selectedNote} />}
            </div>

            {notes.length > 0 && (
              <div ref={allNotesSectionRef} className="mt-8 pt-6">
                <h3 className="body-16-semibold mb-4">All notes</h3>
                <ul className="list-none space-y-2 p-0">
                  {notes.map((note) => {
                    const riskLevelDotClassName =
                      RISK_LEVEL_DISPLAY[note.riskLevel as RiskLevel]
                        ?.dotClassName ?? "bg-neutral-50";
                    const isSelected = note.id === selectedNoteId;

                    return (
                      <li key={note.id}>
                        <Button
                          type="button"
                          variant="default"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => {
                            setShouldScrollToDetail(true);
                            openDrawer(note.id);
                          }}
                          className={cn(
                            "h-auto w-full justify-between gap-4 rounded-lg px-4 py-3 duration-200 transition-all ease-in-out",
                            isSelected
                              ? "bg-blue-15"
                              : "bg-blue-10 hover:bg-blue-15",
                          )}
                        >
                          <div className="flex min-w-0 items-center gap-2">
                            <div className="shrink-0 rounded-full bg-turquoise-10 p-1 text-turquoise-100">
                              <OneOnOneNotesIcon />
                            </div>
                            <span className="body-14-regular truncate">
                              1-on-1 Notes on{" "}
                              {formatCatchUpDate(note.lastCatchUp)}
                            </span>
                          </div>
                          <div className="space-y-1 text-left min-w-[12rem]">
                            <p className="body-12-regular text-neutral-50">
                              Risk Level
                            </p>
                            <div className="flex items-center gap-2">
                              <span
                                className={cn(
                                  "h-2.5 w-2.5 rounded-full",
                                  riskLevelDotClassName,
                                )}
                              />
                              <span className="body-14-regular">
                                {note.riskLevel}
                              </span>
                            </div>
                          </div>
                        </Button>
                      </li>
                    );
                  })}
                </ul>
                {isFetchingNextPage && (
                  <div className="mt-4">
                    <LoadingIndicator />
                  </div>
                )}
                {showViewMore && !isFetchingNextPage && (
                  <Button
                    variant="link"
                    className="mt-4 h-auto p-0"
                    onClick={onViewMore}
                  >
                    View more
                  </Button>
                )}
              </div>
            )}
          </LoadingWrapper>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default OneOnOneNotesSheet;
